'use client';

/**
 * PlaceMenuBody — the two bodies a public menu page can wear, and the choice
 * between them.
 *
 * TWO BODIES, ONE SHELL. The shell around them is always the page's PlaceShell:
 * header, ribbon, nav, rail and footer, sheet dialogs, one promotion fallback
 * slot. Only the body inside it changes.
 *
 *   · Menuraq's own body (`PlaceMenuList`) — legible, orderable, scrolls.
 *   · The template's own body (`TemplateRenderer`) — the designer's palette,
 *     type and cards, inside the shell's scroll region, so the merchant's
 *     authored design actually shows up on the customer surface. A paged-flip
 *     template renders its real flipbook, which keeps its pager as the body.
 *
 * ORDERING NEVER DEPENDS ON THE BODY. The island captures `[data-item-id]`
 * taps at the document level, so dishes stay orderable no matter which engine
 * painted them.
 *
 * THE PLATFORM BODY IS THE FLOOR. `showTemplateBody` is false whenever the
 * template has nothing to paint, so a template assigned to a branch can never
 * cost the guest the menu.
 *
 * WHY THIS IS ITS OWN FILE. The fork used to live inside `MenuDisplayPage`,
 * which is already a 4,000-line page (file-budget P7-11 says: split it, do not
 * grow it). The fork is the one part of that page with a real reason to be
 * read on its own — it is the whole contract between "the menu" and "the
 * design", and it is the piece the switcher changes.
 *
 * @location components/place/PlaceMenuBody.tsx
 */

import { TemplateRenderer } from '@/app/dashboard/templates/p/TemplateRenderer';
import type { MenuData, Template, TemplateThemeMode } from '@/app/dashboard/templates/components/types/template';
import { useMemo } from 'react';
import QueryErrorState from '@/components/QueryErrorState';
import { GuestV2MenuList } from '@/components/place/GuestV2MenuList';
import { PlaceMenuList, type PlaceMenuLayout, type PlaceMenuSection } from '@/components/place/PlaceMenuList';
import { Skeleton } from '@/components/ui/skeleton';
import { EMPTY_DIETARY_PROFILE, type DietaryProfile } from '@/lib/menu/dietary';
import type { OrderingMenuItem } from '@/lib/menu/ordering/types';
import { getFriendlyQueryErrorMessage } from '@/lib/query-resilience';
import { ensureTemplateAppearanceVariants } from '@/lib/menu-themes/apply';

export function PlaceMenuBody({
  showTemplateBody,
  experienceV2 = false,
  template,
  data,
  themeMode,
  loadError,
  onRetry,
  loadingPalette,
  sections,
  layout,
  onOpenItem,
  onAddItem,
  dietaryProfile = EMPTY_DIETARY_PROFILE,
  hideDietaryClashes = false,
  searchQuery,
  onClearSearch,
  emptyMessage,
}: {
  /** True when the template's own body paints the screen. */
  showTemplateBody: boolean;
  /** V2 draws the platform body as the Chow list, not `PlaceMenuList`. */
  experienceV2?: boolean;
  /** The template being shown (the branch's own, or the previewed one). */
  template: Template;
  /** The menu data the template engine renders. Undefined while it loads. */
  data: MenuData | undefined;
  /** The page's active appearance mode, shared by both template engines. */
  themeMode: TemplateThemeMode;
  /** Set when the menu failed to load — the body shows the error, not a skeleton. */
  loadError: unknown;
  onRetry: () => void;
  /** The palette the skeleton is drawn in, so it matches the template's canvas. */
  loadingPalette: { background: string; foreground: string; accent: string };
  /** The platform body's sections. */
  sections: PlaceMenuSection[];
  layout: PlaceMenuLayout;
  onOpenItem: (item: OrderingMenuItem) => void;
  /** Omitted when the branch cannot take an order — the row then has no add control. */
  onAddItem?: (item: OrderingMenuItem) => void;
  /** V2 only: the device-local profile that labels each dish. */
  dietaryProfile?: DietaryProfile;
  /** V2 only: the guest pressed "Hide what I can't eat". */
  hideDietaryClashes?: boolean;
  /** V2 only: the query in the inline field, for the honest no-results state. */
  searchQuery?: string;
  onClearSearch?: () => void;
  emptyMessage?: string;
}) {
  const appearanceTemplate = useMemo(
    () => ensureTemplateAppearanceVariants(template),
    [template],
  );

  if (data) {
    if (showTemplateBody) {
      return (
        <TemplateRenderer
          template={appearanceTemplate}
          data={data}
          forcedThemeMode={themeMode}
          // No in-page theme toggle on the customer surface: the shell is the
          // chrome, and a floating switch would collide with its header. The
          // page's account appearance switch owns the mode for this body too.
          showThemeToggle={false}
          themeTogglePosition="top-right"
          // The flipbook sizes itself against the viewport. Offset by the
          // shell's own top chrome (56px header + 34px ribbon) so the book
          // fits the scroll region instead of hiding under it. Legacy
          // templates ignore the offset and just fill the body scroll.
          viewportOffsetPx={90}
          // The shell's bottom rail is ~69px on desktop; lift the flipbook's
          // fixed pager pills (page indicator, swipe hint) above it. Mobile
          // already reserves 144px (`max-sm:bottom-36`), so this only moves
          // the sm+ positions. Legacy templates ignore this.
          bottomChromeOffsetPx={72}
        />
      );
    }

    return experienceV2 ? (
      <GuestV2MenuList
        sections={sections}
        layout={layout}
        onOpenItem={onOpenItem}
        onAddItem={onAddItem}
        dietaryProfile={dietaryProfile}
        hideDietaryClashes={hideDietaryClashes}
        searchQuery={searchQuery}
        onClearSearch={onClearSearch}
        emptyMessage={emptyMessage}
      />
    ) : (
      <PlaceMenuList
        sections={sections}
        headingFont="var(--mnrq-template-font-heading)"
        layout={layout}
        onOpenItem={onOpenItem}
        onAddItem={onAddItem}
      />
    );
  }

  if (loadError) {
    return (
      <div className="mx-auto flex min-h-[50vh] w-full max-w-[980px] items-center justify-center px-4 py-10">
        <QueryErrorState
          title="Menu data could not load"
          description={getFriendlyQueryErrorMessage(
            loadError,
            'We could not load this menu right now.',
          )}
          onRetry={onRetry}
          onReload={() => window.location.reload()}
          className="w-full max-w-xl border-border/70 bg-card"
        />
      </div>
    );
  }

  return (
    <div
      className="mx-auto flex min-h-[50vh] w-full max-w-[980px] items-center justify-center px-4"
      style={{ backgroundColor: loadingPalette.background, color: loadingPalette.foreground }}
    >
      <div className="w-full max-w-md space-y-4">
        <Skeleton className="h-6 w-48" style={{ backgroundColor: `${loadingPalette.accent}20` }} />
        <Skeleton className="h-4 w-full" style={{ backgroundColor: `${loadingPalette.accent}20` }} />
        <Skeleton className="h-4 w-3/4" style={{ backgroundColor: `${loadingPalette.accent}20` }} />
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="h-32 rounded-xl" style={{ backgroundColor: `${loadingPalette.accent}15` }} />
          <div className="h-32 rounded-xl" style={{ backgroundColor: `${loadingPalette.accent}15` }} />
        </div>
      </div>
    </div>
  );
}
